import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ResponseForm} from '../models/responsedemat.model';
import {SnackBarToastyService, typeToast} from '@Common/widgets/general/snack-bar';
import {ResponsesService} from '../../../services/responses/responses.service';
import {ResponseStateGestModel} from './response-state-gest-model';
import {MatSelectChange} from '@angular/material/select';
import {FormModel, FormState, FormType, ReponseState, ResponseStateInstruction} from '../models';
import {SubSink} from '@Common/core/utils/subsink';
import {concatMap, finalize, pairwise, startWith} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {DialogData, DialogValidationComponent} from '@Common/widgets/general/dialog-validation';
import {UntypedFormControl} from '@angular/forms';
import {InstructionService} from '../../../services/instructions/instruction.service';
import {ResponseInstruction} from '../../../services/instructions/response-instruction.model';
import {Constants} from '../form-builder/common/constants';
import {ModalInstructionMotifComponent} from "../modal-instruction-motif/modal-instruction-motif.component";
import {
  OpinionRequesterCopyRecipients
} from "../../../../gestion/reponses/reponse-fiche/request-opinion/opinion-requester.model";

/**
 * Etat d'une demande / réponse
 *
 * Un gestionnaire peut modifier l'état d'une demande envoyé par un demandeur :
 * - en traitement
 *
 * à partir de cet état :
 * - incomplète : permettre au demandeur de modifier sa demande, la réponse / demande sera remise en "brouillon"
 * - accepté
 * - refusé
 * - classe sans suite
 *
 * à partir de Incomplète :
 * - classer sans suite afin de pouvoir l'archiver, si le demandeur ne répondait pas
 *
 * un message, facultatif, peut être adossé à la décision
 */
@Component({
  selector: 'app-response-instruction-state',
  templateUrl: './response-state-instruction.component.html',
  styleUrls: ['./response-state-instruction.component.scss']
})
export class ResponseStateInstructionComponent implements OnInit {
  @Input() form: FormModel;
  @Input() response: ResponseForm;
  @Input() gestionnaireId: number;
  @Input() align: 'start' | 'center' | 'end' = 'end';
  @Output() onChangedState: EventEmitter<ResponseStateInstruction> = new EventEmitter<ResponseStateInstruction>();
  stateCurrent: ResponseStateGestModel;
  previousState: ResponseStateGestModel;
  states: ResponseStateGestModel[] = [];
  progress = false;
  control = new UntypedFormControl();
  subSink = new SubSink();
  constants: any = Constants;

  constructor(private dialog: MatDialog,
              private snakeSvc: SnackBarToastyService,
              private instructionSvc: InstructionService,
              private responseSvc: ResponsesService) { }

  ngOnInit(): void {
    this._initObserverOldValue();
    this._initStatesLabel();

  }
  /**
   * pour pouvoir remettre l'état d'avant de l'instruction en cas d'annulation de l'action
   */
  private _initObserverOldValue() {
    this.control.valueChanges.pipe(
      startWith([this.control.value]),
      pairwise()
    ).subscribe(([old, value]) => this.previousState = old);
  }
  /**
   * Peut-il redonner la main au demandeur ?
   * @param currentState
   * @param states
   * @private
   */
  private _setStateNull(currentState, states) {
    if (currentState === ResponseStateInstruction.in_progress || currentState === null) {
      const label = currentState === ResponseStateInstruction.in_progress ? 'Repasser en construction pour le demandeur' : 'En attente';
      const explain = currentState === ResponseStateInstruction.in_progress ?
        'Vous permettez au demandeur de modifier ses réponses au formulaire' : 'Le demandeur est en attente de traitement de sa demande';
      const reinit = <ResponseStateGestModel>{
        order: -1,
        icon: 'backspace', color: 'black',
        state: null,
        label: label, explain: explain
      };
      states = [...[reinit], ...states];
    }
    return states;
  }
  isDisabled() {
    if (this.response.form_obj.type === FormType.Sondage) {
      return true;
    }
    if (this.response?.state === ReponseState.archived ||
        this.response.form_obj.state === FormState.archived
    ) {
      return true;
    }
    return this.progress || ! this.gestionnaireId;
  }
  stateTooltip() {
    if (this.stateCurrent?.state === ResponseStateInstruction.incomplete) {
      return 'la demande est incomplète, le demandeur doit la compléter et la renvoyer avant de pouvoir changer son statut';
    }
    if (this.response?.state === ReponseState.archived) {
      return 'la demande est archivée, veuillez la désarchiver afin de pouvoir interagir dessus';
    }
    return 'Veuillez choisir le nouveau statut pour cette demande';
  }
  /**
   * Calcul des états possible d'instruction selon l'état courant
   *
   * null : en attente => in_progress
   * in_progress (en traitement) => accepted / refused / closed / incomplete
   * incomplete : reste en incomplète - le demandeur doit la renvoyer pour traitement => closed (sans suite) possible
   * accepted / refused / closed => in_progress
   * @param {ResponseForm} response
   */
  private _calculateStateInstruction(response: ResponseForm) {
    let states: ResponseStateGestModel[] = [];
    if (response.last_instruction_obj) {
      const currentState = response.last_instruction_obj?.state;

      if (currentState === ResponseStateInstruction.in_progress) { // en traitement
        states = this.responseSvc.statesResponseInstruction([ResponseStateInstruction.incomplete,
          ResponseStateInstruction.in_progress,
          ResponseStateInstruction.accepted, ResponseStateInstruction.refused, ResponseStateInstruction.closed],
          this.form);
      }

      if (currentState === null) { // en attente de traitement
        states = this.responseSvc.statesResponseInstruction([ResponseStateInstruction.in_progress],
                                                            this.form);
      }

      if ([ResponseStateInstruction.accepted,
           ResponseStateInstruction.refused,
           ResponseStateInstruction.closed].includes(currentState)) { // acceptée / refusée / sans suite
        states = this.responseSvc.statesResponseInstruction([currentState, ResponseStateInstruction.in_progress],
                                                            this.form);
      }

      // TG-641 : une incomplète peut être mise en sans suite si le demandeur ne répondait jamais
      // fix TG-596 : une demande en incomplète ne peut être mise en traitement, elle reste en état
      if (currentState === ResponseStateInstruction.incomplete) { // incomplète
        states = this.responseSvc
          .statesResponseInstruction([ResponseStateInstruction.incomplete,
                                      ResponseStateInstruction.closed], this.form);
      }
    } else { // aucune instruction encore
      states = this.responseSvc.statesResponseInstruction([ResponseStateInstruction.in_progress], this.form);
      states = this._setStateNull(null, states);
    }
    console.log(states);
    return states;
  }
  /**
   * rien (demande envoyée) => Passage en traitement possible
   * A partir de l'instruction en traitement =>
   * - incomplète / remettre en construction pour le demandeur
   * - acceptée
   * - refusée
   * - sans suite
   * A partir de incomplète
   * - sans suite
   * @private
   */
  private _initStatesLabel(response: ResponseForm = this.response) {
    this.states = this._calculateStateInstruction(response);
    let findState;
    // positionnement de l'état courant de l'instruction
    if (response.last_instruction_obj) {
      findState = this.states.find(_state => _state.state === response.last_instruction_obj?.state);
    } else {
      findState = this.states.find(_state => _state.state === null);
    }
    if (findState) {
      this.stateCurrent = findState;
    }
  }

  /**
   * Le passage en état "Incomplete" impose que la réponse soit de nouveau modifiable
   * @param {ResponseStateInstruction} state
   * @param {ResponseForm} response
   * @private
   */
  private _updateResponseDraftIfIncomplete(state: ResponseStateInstruction, response: ResponseForm) {
    if (state === ResponseStateInstruction.incomplete) {
      response.state = ReponseState.draft;
      this.subSink.sink = this.responseSvc.patch(response, 'uuid').subscribe(_response => this.response = _response);
    }
  }

  /**
   * Le passage de incomplet à classée sans suite implique que la demande n'est plus en brouillon
   * -> elle passe en publiée
   * @private
   */
  private _updateResponseSendIfSansSuite(state: ResponseStateInstruction, response: ResponseForm) {
    // ajout test qu'elle soit en brouillon pour le classement sans suite, évite un appel pour rien si elle venait du statut "en traitement"
    if (state === ResponseStateInstruction.closed && response.state === ReponseState.draft) {
      response.state = ReponseState.published;
      this.subSink.sink = this.responseSvc.patch(response, 'uuid').subscribe(_response => this.response = _response);
    }
  }
  /**
   * Mise à jour état et rechargement réponse pour mise à jour de la liste des états possibles
   * l'état "Incomplète" provoque la remise en "brouillon" de la demande afin que le demandeur puisse la modifier
   * @param {ResponseStateInstruction} state : état à mettre à jour
   * @param {string} message : message / motification pour l'acceptation / refus / sans suite
   * @param {OpinionRequesterCopyRecipients[]} cc : mise en copie
   * @private
   */
  private _updateInstruction(state: ResponseStateInstruction, message: string = '', cc: OpinionRequesterCopyRecipients[]= []) {
    this.progress = true;
    const instruction: ResponseInstruction = <ResponseInstruction> {
      state: state,
      response: this.response.id,
      gestionnaire: this.gestionnaireId,
      message: message,
      cc_emails: cc.map(person => person.email).join(',')
    };
    this.subSink.sink = this.instructionSvc
      .create(instruction)
      .pipe(concatMap(_ins => this.responseSvc.get(this.response.uuid)),
            finalize(() => this.progress = false))
      .subscribe(_response => {
        this.response = _response;
        this._updateResponseDraftIfIncomplete(state, this.response);
        this._updateResponseSendIfSansSuite(state, this.response);
        this._initStatesLabel();
        this.snakeSvc.toasty('Instruction demande', `Le statut de la demande a bien été modifié`, typeToast.success);
        this.instructionSvc.instructionChangedSubject.next(true);
    });
  }
  /**
   * Traitement des états acceptée / refusée / sans suite : possibilité d'un message
   * @param state
   */
  private _stateMessage(state: ResponseStateInstruction) {
    const stateLabel = this.responseSvc.stateInstructionLabel(state, this.form).toLowerCase();
    const optionnelOrRequired = this.responseSvc.optionalOrRequiredLabel(state, this.constants.MIN_MOTIF_LENGTH);
    const data = new DialogData();
    data.title = `Instruction : demande ${stateLabel}`;
    data.message = `La raison ou motif du changement de statut de la demande (${optionnelOrRequired[0]})`;
    data.contentPlaceHolder = `Expliquez au demandeur pourquoi sa demande est ${stateLabel} (${optionnelOrRequired[1]})`;
    data.disableButton = this.responseSvc.isMotifStateRequired(state);
    data.contentMinLength = this.constants.MIN_MOTIF_LENGTH;
    const _data = {
      data: data,
      formId: this.form.id,
      stateToChange: state
    }
    const dialogRef = this.dialog.open(ModalInstructionMotifComponent, { data: _data });
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        console.log(result);
        this._updateInstruction(state, result.contentData, result.personsInCopy || []);
        this.onChangedState.emit(state);
      } else {
        this.stateCurrent = this.previousState;
      }
    });
  }
  /**
   * Confirmation statut en traitement ou remise au demandeur
   * @param state
   * @private
   */
  private _stateValidation(state) {
    const data = new DialogData();
    data.title = 'Instruction';
    data.message = 'Confirmez-vous ce changement de statut ?';
    data.iconType = 'check_circle';

    const dialogRef = this.dialog.open(DialogValidationComponent, { data: data });
    dialogRef.updatePosition({top: '50px'});
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this._updateInstruction(state, result.contentData);
        this.onChangedState.emit(state);
      } else {
        this.stateCurrent = this.previousState;
      }
    });
  }
  onSelect(event: MatSelectChange) {
    const state = event.value.state;
    if ([ResponseStateInstruction.accepted, ResponseStateInstruction.refused, ResponseStateInstruction.closed,
      ResponseStateInstruction.incomplete].includes(state)) {
      this._stateMessage(state);
    } else {
      this._stateValidation(state);
    }
  }
}
